<template>
    <div :class="$style.leaderboard">
        <div :class="$style.titles">
            <Typography
                is="h2"
                type="header"
                size="xs"
                :responsive="false"
            >
                {{ $tr('gameDetails.leaderboard.title') }}
            </Typography>
            <div :class="$style.subtitle">
                <Typography
                    type="label"
                    size="xs"
                    :responsive="false"
                >
                    {{ $tr('gameDetails.leaderboard.subtitle', { total: formatValue(props.totalPlayers) }) }}
                </Typography>
                <BaseLink
                    :to="{ name: ROUTE_NAMES.LEADERBOARDS_INFO }"
                    target="_blank"
                    :title="$tr('gameDetails.leaderboard.info')"
                    :aria-label="$tr('gameDetails.leaderboard.info')"
                    :class="$style.info_override"
                >
                    <Icon
                        name="info"
                        :class="$style.infoIcon"
                    />
                </BaseLink>
            </div>
        </div>
        <ol
            :class="[
                $style.players,
                userSlot === 'both' && $style.slot_both,
                userSlot === 'short' && $style.slot_short,
            ]"
            role="list"
        >
            <li
                v-for="row in rows"
                :key="row.isUserRow ? 'user' : row.rank"
                :class="[row.isUserRow ? $style.user_row : $style.row, row.isUser && $style.highlight]"
            >
                <div :class="$style.player">
                    <Typography
                        type="label"
                        size="s"
                        accent
                        :responsive="false"
                        :class="$style.rank"
                    >
                        {{ row.rank }}
                    </Typography>
                    <PresetAvatar
                        :avatar="getAvatarIndex(row.display_name)"
                        :color="getAvatarColorIndex(row.display_name)"
                        :class="$style.avatar_override"
                    />
                    <Typography
                        type="label"
                        size="s"
                        accent
                        :responsive="false"
                        :class="$style.name"
                    >
                        {{ shortenDisplayName(row.display_name)
                        }}<span
                            v-if="row.isUser"
                            :class="$style.you"
                        >
                            &#8226; {{ $tr('gameDetails.leaderboard.you') }}</span
                        >
                    </Typography>
                </div>
                <div :class="[$style.score, row.rank === 1 && $style.golden]">
                    <Icon
                        name="star"
                        :class="$style.star"
                    />
                    <Typography
                        type="label"
                        size="s"
                        accent
                        :responsive="false"
                    >
                        {{ formatValue(row.score) }}
                    </Typography>
                </div>
            </li>
        </ol>
    </div>
</template>
<script lang="ts" setup>
import { computed, toRef } from 'vue'

import BaseLink from '@/components/BaseLink.vue'
import Icon from '@/components/Icon/Icon.vue'
import PresetAvatar from '@/components/PresetAvatar/PresetAvatar.vue'
import Typography from '@/components/Typography.vue'
import { ROUTE_NAMES } from '@/router/constants'
import type { LeaderboardEntry } from '@/types'
import { formatValue } from '@/utils/numbers'

import { getAvatarColorIndex, getAvatarIndex, shortenDisplayName, getUserSlot } from './helpers'
import { useLeaderboardUser } from './use-leaderboard-user'

const props = defineProps<{ entries: LeaderboardEntry[]; totalPlayers: number; hru: string }>()

const user = useLeaderboardUser(
    toRef(() => props.hru),
    toRef(() => props.entries),
)

const userSlot = computed(() => getUserSlot(user.value?.rank, props.entries))

const rows = computed(() => {
    const rank = user.value?.rank
    const list = props.entries.map((entry) => ({ ...entry, isUser: entry.rank === rank, isUserRow: false }))
    if (user.value && userSlot.value !== 'none') {
        list.push({ ...user.value, isUser: true, isUserRow: true })
    }
    return list
})
</script>
<style module>
.leaderboard {
    display: flex;
    flex-direction: column;
    gap: var(--gap-16);
    padding: 12px 8px 8px;
    border-radius: var(--radius-12);
    background: var(--carbon-400);
    color: var(--white);
}

.titles {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-left: 8px;
    min-width: 0;
}

.subtitle {
    display: flex;
    align-items: center;
    gap: var(--gap-4);
    color: var(--white-40);
}

.info_override {
    display: flex;
}

.info_override:hover {
    color: var(--white);
}

.infoIcon {
    width: 12px;
    height: 12px;
    transform: translateY(1px);
}

.players {
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    list-style: none;
}

.row,
.user_row {
    align-items: center;
    justify-content: space-between;
    gap: var(--gap-8);
    padding: 6px 8px;
    border-radius: var(--radius-16);
}

.row {
    display: flex;
}

.user_row {
    display: none;
}

.highlight {
    background: var(--white-08);
}

.you {
    color: var(--white-40);
}

.row:nth-child(n + 6) {
    display: none;
}

.slot_short .row:nth-child(n + 5),
.slot_both .row:nth-child(n + 5) {
    display: none;
}

.slot_short .user_row,
.slot_both .user_row {
    display: flex;
}

.player {
    display: flex;
    flex: 1;
    align-items: center;
    gap: var(--gap-8);
    min-width: 0;
}

.rank {
    flex: 0 0 16px;
    text-align: center;
}

.avatar_override {
    flex-shrink: 0;
}

.name {
    display: block;
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.score {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: var(--gap-4);
}

.star {
    width: 12px;
    height: 12px;
}

.golden {
    color: var(--golden-400);
}

@container (min-width: 668px) {
    .row:nth-child(n + 6) {
        display: flex;
    }

    .row:nth-child(n + 9) {
        display: none;
    }

    .slot_short .row:nth-child(n + 5) {
        display: flex;
    }

    .slot_short .row:nth-child(n + 9) {
        display: none;
    }

    .slot_short .user_row {
        display: none;
    }

    .slot_both .row:nth-child(n + 5) {
        display: flex;
    }

    .slot_both .row:nth-child(n + 8) {
        display: none;
    }
}
</style>
